import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { distinctUntilChanged, startWith, switchMap, tap } from 'rxjs/operators';
import { Unsubscribable } from 'rxjs';
import * as _ from 'lodash';
import { FileUploadControl } from '@iplab/ngx-file-upload';
import { DmsService } from 'app/shared/common/utils/DmsService';
import { UtilComponent } from 'app/shared/common/utils/UtilComponent';
import { FileUploadUtils } from './FileUploadUtils';
import { Events } from 'app/shared/common/utils/Events';

@Component({
    selector: 'envelop-files-detail-widget',
    template: `
    <div class="upload-file-ext upload-file-ext2" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px" style="width: '100%'">
        <file-upload [formControl]="uploadFormControl" [style.width]="'100%'" [control]="uploadControl"
            [accept]="options?.accept" [maxsize]="options?.maxsize" [minsize]="1" [fileslimit]="options?.fileslimit"
            [animation]="false" class="file-upload-ext">
            <ng-template let-i="index" let-file="file" #item>
                <div class="file-container" fxLayout="row" fxLayoutGap="5px" fxLayoutAlign="start center">
                    <div class="file-info" fxFlex fxLayout="row" fxLayoutGap="5px" fxLayoutAlign="start center">
                        <file-upload-type-icon [file]="file"></file-upload-type-icon>
                        <span *ngIf="isPdfFile(file)">
                                <span class="file-name" (click)="onPdfFileClick(file)">{{ viewFile(file) }} <span class="file-size">{{ renderSize(file.size) }}</span></span>
                            </span>
                            <span *ngIf="!isPdfFile(file)">
                                <span>{{ file.name }} <span class="file-size">{{ renderSize(file.size) }}</span></span>
                            </span>
                    </div>
                    <div class="file-buttons" fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="5px">
                        <div *ngIf="file.downloading" class="downloading" fxLayout="row" fxLayoutAlign="center center">
                            <mat-progress-bar mode="buffer"></mat-progress-bar>
                        </div>
                        <div *ngIf="!file.downloading && file.identifier">
                            <button mat-icon-button class="btn-icon" (click)="downloadFile(file)">
                                <mat-icon>get_app</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </ng-template>
        </file-upload>
    </div>
    `,
    styles: [`
        .upload-file-ext{
            .list-visible{
                .upload-input {
                    display:none !important;
                }
            }
            .list-visible[_nghost-pya-c239] .upload-input[_ngcontent-pya-c239]{
                display:none !important;
            }
            file-upload-drop-zone{
                display: none !important;
                cursor: none !important;
            }
        }
        file-upload .file-container {
            min-height: 40px;
        }

        file-upload-type-icon {
            width: 32px;
            height: 36px;
        }

        file-upload .file-buttons .btn-icon {
            width: 36px;
            height: 36px;
        }

        file-upload .file-buttons mat-icon {
            width: 28px;
            height: 28px;
        }

        file-upload .file-buttons div.uploading {
            width: 72px;
            height: 36px;
        }

        file-upload .file-buttons div.downloading {
            width: 72px;
            height: 36px;
        }

        file-upload .file-buttons div.deleting {
            width: 72px;
            height: 36px;
        }

        file-upload .file-buttons mat-icon:hover {
            color: #80a9d2;
        }

        file-upload.ng-touched.ng-invalid {
            outline-color: #ff0000;
            background-color: #fdf4f4;
        }

        file-upload label file-upload-drop-zone {
            display:none !important;
        }

        .disabled {
            opacity: 1 !important;
            cursor: pointer !important;
        }

        file-upload .file-name {
            cursor: pointer;
            color: #2078F4;
            text-decoration: underline;
        }

        .upload-input {
            display:none !important;
        }
    `],
})
export class EnvelopFilesDetailComponent implements OnInit, OnDestroy {
    formControl: AbstractControl;
    controlName: string;
    controlValue: string;
    controlDisabled = false;
    boundControl = false;
    options: any;

    @Input() layoutNode: any;

    uploadFormControl: FormControl;
    uploadControl = new FileUploadControl();
    private _unsubscribableArray: Array<Unsubscribable> = [];
    public deleteDocIds = [];

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _dmsService: DmsService,
        private _utilComponent: UtilComponent
    ) {
    }

    ngOnInit(): void {
        this.deleteDocIds = [];
        this.options = this.layoutNode.options || {};
        // options.accept = 'image/*,audio/*,.mp4' for all images, audios and mp4.
        this.options.accept = this.options.accept || ''; // default all files
        this.controlDisabled = this.options.controlDisabled;

        this.uploadFormControl = new FormControl([]);
        this.formControl = new FormControl([]);
        if (this.controlDisabled) {
            this.uploadFormControl.disable();
        }

        this._initSubscribe();
    }

    ngOnDestroy(): void {
        this._unsubscribableArray.forEach(s => {
            if (s && s.unsubscribe) {
                s.unsubscribe();
            }
        });
    }

    public getUploadFormControl(): any {
        return this.uploadFormControl;
    }

    public downloadFile(file: any): void {
        if (file) {
            file.downloading = true;
            this._changeDetectorRef.detectChanges();
            setTimeout(() => {
                const { identifier, name } = file;

                this._dmsService.getPdfMultiPartContentEnvelop(identifier).then(blob => {
                    if (!blob) {
                        this._utilComponent.showTranslateSnackbar('FILE_NOT_FOUND', 'error');
                    } else {
                        let nameFinal = name;
                        if (blob?.type === 'application/pdf') {
                            let nameArr = name.split('.');
                            nameFinal = '';
                            for (let i = 0; i < nameArr.length - 1; i++) {
                                nameFinal += (nameArr[i] + '.');
                            }
                            nameFinal += 'pdf';
                        }
                        this._dmsService.saveAsBlob(blob, nameFinal);
                    }
                    file.downloading = false;
                    this._changeDetectorRef.detectChanges();
                }).catch(error => {
                    console.error(`Error when download file: ${file.identifier}`, error);
                    this._utilComponent.showTranslateSnackbar('FILE_NOT_FOUND', 'error');
                    file.downloading = false;
                    this._changeDetectorRef.detectChanges();
                });
            }, 100);
        }
    }

    public uploadFiles(files): Promise<any> {
        if (_.isEmpty(files)) {
            return Promise.resolve([]);
        }

        return Promise.all(
            _.map(files, async (file) => {
                if (file.identifier) {
                    return file;
                }

                try {
                    const formData: FormData = new FormData();
                    formData.append('file', file, file.name);
                    formData.append('envelopeAggId', this.options.envelopAggId + '');
                    const resData = await this._dmsService.postMultiPartContentEnvelop(formData, file.name);
                    if (resData && resData.errorcode) {
                        return undefined;
                    }

                    file.uploading = false;
                    file.identifier = resData;
                    if (file.size === 0) {
                        this._utilComponent.openSnackBar('Upload file failed!', 'error');
                        return undefined;
                    }
                    return file;
                } catch (e) {
                    console.error(`Error when read or upload file: ${file.name}`, e);
                    this._utilComponent.showTranslateSnackbar('SYSTEM_ERROR', 'error');
                    return undefined;
                }
            })
        );
    }

    public renderSize(size: number): string {
        if (size > 0) {
            return `(${FileUploadUtils.calculateSize(size)})`;
        }
        return '';
    }

    private _initSubscribe(): void {
        this._unsubscribableArray.push(
            // formControl of json form (model) -> uploadFormControl (UI)
            this.formControl.valueChanges.pipe(
                startWith(this.controlValue),
                switchMap(strValue => {
                    let array = [];
                    try {
                        if (_.isString(strValue)) {
                            array = JSON.parse(strValue);
                        }

                        array = _.filter(array, f => !_.isEmpty(f.identifier));
                    } catch (e) {
                        console.error('Can not parse json', e);
                    }

                    if (_.isEmpty(array) || _.every(array, f => !_.isEmpty(f.name))) {
                        return Promise.resolve(array);
                    }

                    const refCodes = _.map(array, f => f.identifier);
                    return this._dmsService.getResourcesEnvelop(refCodes).then(resources => {
                        _.forEach(array, f => {
                            const resource = _.find(resources, r => r.refCode === f.identifier);
                            if (resource) {
                                f.name = resource.fileName;
                                if (resource.size > 0) {
                                    f.size = resource.size;
                                }
                                f.type = resource.mimeType;
                            }
                        });
                        return array;
                    }).catch((error) => {
                        console.error('Can\'t not get resources', error);
                        return array;
                    });
                }),
                distinctUntilChanged(FileUploadUtils.isEqualFiles)
            ).subscribe(files => {
                // avoid update 2 times
                if (!FileUploadUtils.isEqualFiles(this.uploadFormControl.value, files)) {
                    this.uploadFormControl.setValue(files);
                    this._changeDetectorRef.detectChanges();
                }
            })
        );
    }

    isPdfFile(file): boolean {
        if (!(file && file.type && file.name)) {
            return false;
        }
        return file.type.indexOf('application/pdf') > -1 || file.name.indexOf('.doc') > -1;
    }

    onPdfFileClick(file): void {
        Events.publish('event:viewPdfFile', { 'file': file, 'type': 'ENVELOPE' });
    }

    viewFile(file): string {
        let name = file.name;
        let nameArr = name.split('.');
        let nameFinal = '';
        for (let i = 0; i < nameArr.length - 1; i++) {
            if (i === 0) {
                nameFinal += nameArr[i];
            } else {
                nameFinal += ('.' + nameArr[i]);
            }
        }
        return nameFinal;
    }
}
